import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  BookOpen, HelpCircle, Plus, Edit2, Trash2, Eye, Search, RefreshCw, Tag, BarChart3,
  CheckCircle, XCircle, Clock, AlertCircle
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

const KB_CATEGORIES = ["General", "Billing", "Claims", "Policy", "Technical", "Account", "Getting Started"];
const FAQ_CATEGORIES = ["General", "Billing", "Claims", "Policy", "Account"];

export default function AgentKnowledgeBase() {
  const [activeTab, setActiveTab] = useState("kb");
  
  // KB State
  const [kbArticles, setKbArticles] = useState([]);
  const [kbStats, setKbStats] = useState(null);
  const [kbLoading, setKbLoading] = useState(true);
  const [kbSearch, setKbSearch] = useState("");
  
  // FAQ State
  const [faqs, setFaqs] = useState([]);
  const [faqLoading, setFaqLoading] = useState(true);
  
  // Dialog states
  const [kbDialogOpen, setKbDialogOpen] = useState(false);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Check if user is admin
  const agentData = JSON.parse(localStorage.getItem("agent_data") || "{}");
  const isAdmin = agentData.role === "super_admin" || agentData.role === "admin";
  
  // Form states
  const [kbForm, setKbForm] = useState({
    title: "", content: "", category: "General", tags: "", is_published: false
  });
  const [faqForm, setFaqForm] = useState({
    question: "", answer: "", category: "General", order: 0, is_published: true
  });

  useEffect(() => {
    fetchKBArticles();
    fetchKBStats();
    fetchFAQs();
  }, []);

  const fetchKBArticles = async () => {
    setKbLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/knowledge-base`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKbArticles(response.data);
    } catch (error) {
      toast.error("Failed to load articles");
    } finally {
      setKbLoading(false);
    }
  };

  const fetchKBStats = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/knowledge-base/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKbStats(response.data);
    } catch (error) {
      console.error("Failed to load KB stats");
    }
  };

  const fetchFAQs = async () => {
    setFaqLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/faqs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(response.data);
    } catch (error) {
      toast.error("Failed to load FAQs");
    } finally {
      setFaqLoading(false);
    }
  };

  // KB Actions
  const handleSaveKBArticle = async () => {
    if (!kbForm.title || !kbForm.content) {
      toast.error("Please fill in title and content");
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem("agent_token");
    try {
      const data = {
        ...kbForm,
        tags: kbForm.tags.split(",").map(t => t.trim()).filter(Boolean)
      };
      
      if (editItem) {
        await axios.put(`${API}/agent/knowledge-base/${editItem.article_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/agent/knowledge-base`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article created");
      }
      
      setKbDialogOpen(false);
      resetKBForm();
      fetchKBArticles();
      fetchKBStats();
    } catch (error) {
      toast.error("Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKBArticle = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/knowledge-base/${deleteTarget.article_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Article deleted");
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchKBArticles();
      fetchKBStats();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const handleApproveArticle = async (article) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/knowledge-base/${article.article_id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Article approved");
      fetchKBArticles();
      fetchKBStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve article");
    }
  };

  const handleRejectArticle = async () => {
    if (!rejectTarget) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/knowledge-base/${rejectTarget.article_id}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Article rejected");
      setRejectDialogOpen(false);
      setRejectTarget(null);
      setRejectReason("");
      fetchKBArticles();
      fetchKBStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject article");
    }
  };

  const getApprovalBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case "pending_approval":
        return <Badge className="bg-orange-100 text-orange-800"><Clock className="h-3 w-3 mr-1" />Pending Approval</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  // FAQ Actions
  const handleSaveFAQ = async () => {
    if (!faqForm.question || !faqForm.answer) {
      toast.error("Please fill in question and answer");
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem("agent_token");
    try {
      if (editItem) {
        await axios.put(`${API}/agent/faqs/${editItem.faq_id}`, faqForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("FAQ updated");
      } else {
        await axios.post(`${API}/agent/faqs`, faqForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("FAQ created");
      }
      
      setFaqDialogOpen(false);
      resetFAQForm();
      fetchFAQs();
    } catch (error) {
      toast.error("Failed to save FAQ");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFAQ = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/agent/faqs/${deleteTarget.faq_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("FAQ deleted");
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchFAQs();
    } catch (error) {
      toast.error("Failed to delete FAQ");
    }
  };

  const resetKBForm = () => {
    setKbForm({ title: "", content: "", category: "General", tags: "", is_published: false });
    setEditItem(null);
  };

  const resetFAQForm = () => {
    setFaqForm({ question: "", answer: "", category: "General", order: 0, is_published: true });
    setEditItem(null);
  };

  const openEditKB = (article) => {
    setEditItem(article);
    setKbForm({
      title: article.title,
      content: article.content,
      category: article.category,
      tags: article.tags?.join(", ") || "",
      is_published: article.is_published
    });
    setKbDialogOpen(true);
  };

  const openEditFAQ = (faq) => {
    setEditItem(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order || 0,
      is_published: faq.is_published
    });
    setFaqDialogOpen(true);
  };

  const filteredKBArticles = kbArticles.filter(article => {
    if (!kbSearch) return true;
    const searchLower = kbSearch.toLowerCase();
    return (
      article.title?.toLowerCase().includes(searchLower) ||
      article.content?.toLowerCase().includes(searchLower) ||
      article.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  return (
    <AgentLayout>
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Young Serif, serif' }}>
            <BookOpen className="h-8 w-8 text-primary" />
            Knowledge Base & FAQs
          </h1>
          <p className="text-muted-foreground mt-1">Manage help content for customers</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="kb" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Knowledge Base
          </TabsTrigger>
          <TabsTrigger value="faq" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            FAQs
          </TabsTrigger>
        </TabsList>

        {/* Knowledge Base Tab */}
        <TabsContent value="kb">
          {/* KB Stats */}
          {kbStats && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <Card className="card-organic">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold">{kbStats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Articles</p>
                </CardContent>
              </Card>
              <Card className="card-organic bg-green-50">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-green-800">{kbStats.published}</p>
                  <p className="text-xs text-green-600">Published</p>
                </CardContent>
              </Card>
              <Card className="card-organic bg-yellow-50">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-yellow-800">{kbStats.drafts}</p>
                  <p className="text-xs text-yellow-600">Drafts</p>
                </CardContent>
              </Card>
              {kbStats.pending_approval > 0 && (
                <Card className="card-organic bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <p className="text-2xl font-bold text-orange-800">{kbStats.pending_approval}</p>
                    <p className="text-xs text-orange-600">Pending Approval</p>
                  </CardContent>
                </Card>
              )}
              <Card className="card-organic bg-blue-50">
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-blue-800">{kbStats.categories_count}</p>
                  <p className="text-xs text-blue-600">Categories</p>
                </CardContent>
              </Card>
              <Card className="card-organic">
                <CardContent className="p-4">
                  <BarChart3 className="h-6 w-6 text-muted-foreground mb-1" />
                  <p className="text-xs text-muted-foreground">Most Viewed</p>
                  <p className="text-sm font-medium truncate">{kbStats.most_viewed?.[0]?.title || "N/A"}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Search and Add */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={kbSearch}
                onChange={(e) => setKbSearch(e.target.value)}
                className="pl-10"
                data-testid="kb-search"
              />
            </div>
            <Button onClick={() => { resetKBForm(); setKbDialogOpen(true); }} data-testid="add-kb-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Article
            </Button>
          </div>

          {/* Articles List */}
          <Card className="card-organic">
            <CardContent className="p-0">
              {kbLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : filteredKBArticles.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Approval</th>
                        <th>Status</th>
                        <th>Views</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKBArticles.map((article) => (
                        <tr key={article.article_id}>
                          <td className="max-w-[300px]">
                            <p className="font-medium truncate">{article.title}</p>
                            {article.tags?.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {article.tags.slice(0, 3).map((tag, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td><Badge variant="secondary">{article.category}</Badge></td>
                          <td>{getApprovalBadge(article.approval_status)}</td>
                          <td>
                            <Badge className={article.is_published ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                              {article.is_published ? "Published" : "Draft"}
                            </Badge>
                          </td>
                          <td className="text-center">{article.views || 0}</td>
                          <td className="text-sm text-muted-foreground">{new Date(article.created_at).toLocaleDateString()}</td>
                          <td>
                            <div className="flex gap-2">
                              {isAdmin && article.approval_status === "pending_approval" && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-green-600 hover:bg-green-50"
                                    onClick={() => handleApproveArticle(article)}
                                    data-testid={`approve-kb-${article.article_id}`}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => { setRejectTarget(article); setRejectDialogOpen(true); }}
                                    data-testid={`reject-kb-${article.article_id}`}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              <Button size="sm" variant="outline" onClick={() => openEditKB(article)} data-testid={`edit-kb-${article.article_id}`}>
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => { setDeleteTarget(article); setDeleteDialogOpen(true); }} data-testid={`delete-kb-${article.article_id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No articles found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faq">
          <div className="flex justify-end mb-6">
            <Button onClick={() => { resetFAQForm(); setFaqDialogOpen(true); }} data-testid="add-faq-btn">
              <Plus className="h-4 w-4 mr-2" />
              New FAQ
            </Button>
          </div>

          <Card className="card-organic">
            <CardContent className="p-0">
              {faqLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : faqs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>Category</th>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faqs.map((faq) => (
                        <tr key={faq.faq_id}>
                          <td className="max-w-[400px]">
                            <p className="font-medium">{faq.question}</p>
                            <p className="text-sm text-muted-foreground truncate">{faq.answer?.substring(0, 100)}...</p>
                          </td>
                          <td><Badge variant="secondary">{faq.category}</Badge></td>
                          <td className="text-center">{faq.order || 0}</td>
                          <td>
                            <Badge className={faq.is_published ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                              {faq.is_published ? "Published" : "Draft"}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => openEditFAQ(faq)} data-testid={`edit-faq-${faq.faq_id}`}>
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => { setDeleteTarget(faq); setDeleteDialogOpen(true); }} data-testid={`delete-faq-${faq.faq_id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <HelpCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No FAQs found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* KB Article Dialog */}
      <Dialog open={kbDialogOpen} onOpenChange={(open) => { setKbDialogOpen(open); if (!open) resetKBForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
              {editItem ? "Edit Article" : "New Article"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={kbForm.title}
                onChange={(e) => setKbForm({...kbForm, title: e.target.value})}
                placeholder="How to file a claim..."
                data-testid="kb-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={kbForm.category} onValueChange={(v) => setKbForm({...kbForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KB_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={kbForm.tags}
                  onChange={(e) => setKbForm({...kbForm, tags: e.target.value})}
                  placeholder="billing, payment, refund"
                />
              </div>
            </div>
            <div>
              <Label>Content * (Supports Markdown)</Label>
              <Textarea
                value={kbForm.content}
                onChange={(e) => setKbForm({...kbForm, content: e.target.value})}
                placeholder="## Introduction&#10;&#10;Describe the topic..."
                rows={12}
                className="font-mono"
                data-testid="kb-content"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={kbForm.is_published}
                onCheckedChange={(checked) => setKbForm({...kbForm, is_published: checked})}
                data-testid="kb-published"
              />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKbDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveKBArticle} disabled={saving} data-testid="save-kb-btn">
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editItem ? "Update" : "Create"} Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={faqDialogOpen} onOpenChange={(open) => { setFaqDialogOpen(open); if (!open) resetFAQForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
              {editItem ? "Edit FAQ" : "New FAQ"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Question *</Label>
              <Input
                value={faqForm.question}
                onChange={(e) => setFaqForm({...faqForm, question: e.target.value})}
                placeholder="How do I...?"
                data-testid="faq-question"
              />
            </div>
            <div>
              <Label>Answer *</Label>
              <Textarea
                value={faqForm.answer}
                onChange={(e) => setFaqForm({...faqForm, answer: e.target.value})}
                placeholder="To do this, you need to..."
                rows={5}
                data-testid="faq-answer"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={faqForm.category} onValueChange={(v) => setFaqForm({...faqForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FAQ_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={faqForm.order}
                  onChange={(e) => setFaqForm({...faqForm, order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={faqForm.is_published}
                onCheckedChange={(checked) => setFaqForm({...faqForm, is_published: checked})}
                data-testid="faq-published"
              />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFaqDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveFAQ} disabled={saving} data-testid="save-faq-btn">
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              {editItem ? "Update" : "Create"} FAQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.article_id ? "Article" : "FAQ"}?</DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={deleteTarget?.article_id ? handleDeleteKBArticle : handleDeleteFAQ}
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Article Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Article</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this article. The author will be notified.
            </DialogDescription>
          </DialogHeader>
          {rejectTarget && (
            <div className="py-2">
              <p className="font-medium">{rejectTarget.title}</p>
              <p className="text-sm text-muted-foreground">by {rejectTarget.author_name}</p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Rejection Reason</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please explain why this article is being rejected..."
              rows={3}
              data-testid="reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialogOpen(false); setRejectReason(""); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectArticle}
              data-testid="confirm-reject-btn"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
